import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, ChevronDown, Plus, Sparkles, Tag as TagIcon, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ManualEntryModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    projectId: '',
    projectName: '',
    description: '',
    hours: 0,
    minutes: 0,
    date: new Date().toISOString().split('T')[0],
    tags: []
  });
  const [useTimeRange, setUseTimeRange] = useState(false);
  const [startHour, setStartHour] = useState('09');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('17');
  const [endMinute, setEndMinute] = useState('00');

  // Generate time options
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minuteOptions = ['00', '15', '30', '45'];
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newProjectData, setNewProjectData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    is_billable: false
  });
  const [aiPredictions, setAiPredictions] = useState([]);

  useEffect(() => {
    if (open) {
      fetchProjects();
      fetchAiPredictions();
    }
  }, [open]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/api/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchAiPredictions = async () => {
    try {
      const response = await axios.post(`${API}/api/projects/predict`, {
        date: new Date().toISOString(),
        time: new Date().toISOString()
      });
      if (response.data && response.data.project_name) {
        setAiPredictions([response.data]); // Wrap in array for consistency
      }
    } catch (error) {
      console.log('AI predictions not available');
    }
  };

  const handleCreateNewProject = async () => {
    if (!newProjectData.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/projects`, newProjectData);
      const newProject = response.data;
      
      // Add to projects list
      setProjects([...projects, newProject]);
      
      // Select the new project
      setFormData({
        ...formData,
        projectId: newProject.id,
        projectName: newProject.name
      });
      
      // Reset new project form
      setNewProjectData({
        name: '',
        description: '',
        color: '#3B82F6',
        is_billable: false
      });
      setShowNewProjectForm(false);
      setShowProjectDropdown(false);
      
      toast.success('Project created successfully!');
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleSelectProject = (project) => {
    setFormData({
      ...formData,
      projectId: project.id,
      projectName: project.name
    });
    setSearchQuery(project.name);
    setShowProjectDropdown(false);
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData({
          ...formData,
          tags: [...formData.tags, tagInput.trim()]
        });
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const calculateDurationFromTimeRange = () => {
    const startTotalMinutes = parseInt(startHour) * 60 + parseInt(startMinute);
    const endTotalMinutes = parseInt(endHour) * 60 + parseInt(endMinute);
    
    let duration = endTotalMinutes - startTotalMinutes;
    if (duration < 0) duration += 24 * 60; // Handle overnight
    
    return duration;
  };

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.projectId) {
      toast.error('Please select a project');
      return;
    }

    let totalMinutes;
    let entryDateTime;

    if (useTimeRange) {
      totalMinutes = calculateDurationFromTimeRange();
      if (totalMinutes <= 0) {
        toast.error('End time must be after start time');
        return;
      }
      // Create datetime with start time
      entryDateTime = `${formData.date}T${startHour}:${startMinute}:00`;
    } else {
      if (formData.hours === 0 && formData.minutes === 0) {
        toast.error('Duration must be greater than 0');
        return;
      }
      totalMinutes = (formData.hours * 60) + formData.minutes;
      // For duration-based entries, use current time or midnight
      entryDateTime = `${formData.date}T00:00:00`;
    }

    setLoading(true);

    try {
      const payload = {
        project_id: formData.projectId,
        description: formData.description.trim(),
        duration_minutes: totalMinutes,
        date: entryDateTime,
        entry_type: 'manual',
        tags: formData.tags
      };

      // Add start_hour and end_hour if using time range
      if (useTimeRange) {
        payload.start_hour = parseInt(startHour);
        payload.end_hour = parseInt(endHour);
      }

      const response = await axios.post(`${API}/api/time-entries/manual`, payload);
      
      if (response.data) {
        toast.success('Time entry created successfully!');
        // Reset form
        setFormData({
          projectId: '',
          projectName: '',
          description: '',
          hours: 0,
          minutes: 0,
          date: new Date().toISOString().split('T')[0],
          tags: []
        });
        setSearchQuery('');
        setTagInput('');
        setUseTimeRange(false);
        setStartHour('09');
        setStartMinute('00');
        setEndHour('17');
        setEndMinute('00');
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error creating manual entry:', error);
      // Handle different error formats
      let errorMessage = 'Failed to create time entry';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-[95vw] md:max-w-[600px] max-h-[85vh] md:max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700 transition-colors">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-700 dark:to-cyan-700 px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0 transition-colors">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <span className="text-xl sm:text-2xl">✍️</span>
              <Dialog.Title className="text-lg sm:text-xl font-semibold text-white transition-colors">
                Manual Time Entry
              </Dialog.Title>
            </div>
            <button 
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto flex-1 bg-white dark:bg-gray-800 transition-colors">
          {/* Project Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
              Project <span className="text-red-500 dark:text-red-400 transition-colors">*</span>
            </label>
            
            {/* AI Predictions */}
            {aiPredictions.length > 0 && !formData.projectId && (
              <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-semibold text-purple-700">AI Suggestions</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {aiPredictions.map((pred, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        const project = projects.find(p => p.name === pred.project_name);
                        if (project) handleSelectProject(project);
                      }}
                      className="px-3 py-1 bg-white text-purple-700 text-xs rounded-full border border-purple-200 hover:bg-purple-100 transition-colors flex items-center gap-1"
                    >
                      {pred.project_name}
                      <span className="text-purple-500 text-xs">
                        {Math.round(pred.confidence * 100)}%
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Project Dropdown */}
            <div className="relative">
              <div
                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-transparent text-base cursor-pointer flex items-center justify-between bg-white dark:bg-gray-700 transition-colors"
              >
                <span className={formData.projectName ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'} transition-colors>
                  {formData.projectName || 'Select a project...'}
                </span>
                <ChevronDown className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${showProjectDropdown ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Dropdown Menu */}
              {showProjectDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto transition-colors">
                  {/* Search Input */}
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700 transition-colors">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search projects..."
                      className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-transparent transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  {/* Create New Project Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewProjectForm(true);
                      setShowProjectDropdown(false);
                    }}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium border-b border-gray-200 dark:border-gray-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Create New Project
                    </button>
                    
                    {/* Projects List */}
                    {filteredProjects.length > 0 ? (
                      filteredProjects.map((project) => (
                        <button
                          key={project.id}
                          type="button"
                          onClick={() => handleSelectProject(project)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
                        >
                          <div
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: project.color }}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-gray-100 transition-colors">{project.name}</div>
                            {project.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate transition-colors">{project.description}</div>
                            )}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 text-sm transition-colors">
                        No projects found
                      </div>
                    )}
                </div>
              )}
            </div>
            
            {!formData.projectId && (
              <p className="text-xs text-red-500 dark:text-red-400 mt-1 transition-colors">Please select a project</p>
            )}
          </div>
          
          {/* New Project Form */}
          {showNewProjectForm && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 transition-colors">Create New Project</h4>
                <button
                  type="button"
                  onClick={() => setShowNewProjectForm(false)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                <input
                  type="text"
                  value={newProjectData.name}
                  onChange={(e) => setNewProjectData({...newProjectData, name: e.target.value})}
                  placeholder="Project name"
                  className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                />
                
                <input
                  type="text"
                  value={newProjectData.description}
                  onChange={(e) => setNewProjectData({...newProjectData, description: e.target.value})}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
                />
                
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Color:</label>
                  <input
                    type="color"
                    value={newProjectData.color}
                    onChange={(e) => setNewProjectData({...newProjectData, color: e.target.value})}
                    className="w-12 h-8 rounded cursor-pointer"
                  />
                  
                  <label className="flex items-center gap-2 ml-auto">
                    <input
                      type="checkbox"
                      checked={newProjectData.is_billable}
                      onChange={(e) => setNewProjectData({...newProjectData, is_billable: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Billable</span>
                  </label>
                </div>
                
                <button
                  type="button"
                  onClick={handleCreateNewProject}
                  className="w-full px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm font-medium"
                >
                  Create Project
                </button>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="What did you work on?"
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 resize-none text-base transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right transition-colors">
              {formData.description.length}/500
            </div>
          </div>

          {/* Tags */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2 transition-colors">
              <TagIcon className="w-4 h-4" />
              Tags
            </label>
            <div className="space-y-2">
              {/* Tag Input */}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Type a tag and press Enter"
                className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 text-base transition-colors placeholder:text-gray-500 dark:placeholder:text-gray-400 font-medium"
              />
              
              {/* Tags Display */}
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors">Press Enter to add a tag</p>
            </div>
          </div>

          {/* Duration Mode Toggle */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 transition-colors">
                <Clock className="w-4 h-4" />
                Duration
              </label>
              <button
                type="button"
                onClick={() => setUseTimeRange(!useTimeRange)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
              >
                {useTimeRange ? 'Use duration' : 'Use time range'}
              </button>
            </div>

            {useTimeRange ? (
              /* Time Range Picker with Dropdowns */
              <div className="space-y-3">
                {/* Start Time */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors">
                    Start Time
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={startHour}
                      onChange={(e) => setStartHour(e.target.value)}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors font-medium"
                    >
                      {hours.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <select
                      value={startMinute}
                      onChange={(e) => setStartMinute(e.target.value)}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors font-medium"
                    >
                      {minuteOptions.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* End Time */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors">
                    End Time
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={endHour}
                      onChange={(e) => setEndHour(e.target.value)}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors font-medium"
                    >
                      {hours.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <select
                      value={endMinute}
                      onChange={(e) => setEndMinute(e.target.value)}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors font-medium"
                    >
                      {minuteOptions.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Duration Display */}
                <div className="text-sm text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 rounded-lg font-medium text-center border border-blue-200 dark:border-blue-800 transition-colors">
                  Duration: {Math.floor(calculateDurationFromTimeRange() / 60)}h {calculateDurationFromTimeRange() % 60}m
                </div>
              </div>
            ) : (
              /* Duration with Increment/Decrement Controls */
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                  Duration <span className="text-red-500 dark:text-red-400 transition-colors">*</span>
                </label>
            
                <div className="flex items-center gap-3">
                  {/* Hours Input */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {/* Decrement Hours */}
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, hours: Math.max(0, formData.hours - 1)})}
                        className="w-11 h-11 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xl cursor-pointer flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-semibold"
                      >−</button>
                  
                      {/* Hours Input Field */}
                      <input
                        type="number"
                        value={formData.hours}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setFormData({...formData, hours: 0});
                          } else {
                            const num = parseInt(val, 10);
                            if (!isNaN(num)) {
                              setFormData({...formData, hours: Math.max(0, Math.min(23, num))});
                            }
                          }
                        }}
                        min="0"
                        max="23"
                        className="flex-1 px-3 py-3 text-base border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-center font-semibold w-[70px] transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400"
                      />
                  
                      {/* Increment Hours */}
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, hours: Math.min(23, formData.hours + 1)})}
                        className="w-11 h-11 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xl cursor-pointer flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-semibold"
                      >+</button>
                    </div>
                    <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors">hrs</div>
                  </div>

                  {/* Minutes Input */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {/* Decrement Minutes */}
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, minutes: Math.max(0, formData.minutes - 1)})}
                        className="w-11 h-11 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xl cursor-pointer flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-semibold"
                      >−</button>
                  
                      {/* Minutes Input Field */}
                      <input
                        type="number"
                        value={formData.minutes}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setFormData({...formData, minutes: 0});
                          } else {
                            const num = parseInt(val, 10);
                            if (!isNaN(num)) {
                              setFormData({...formData, minutes: Math.max(0, Math.min(59, num))});
                            }
                          }
                        }}
                        min="0"
                        max="59"
                        className="flex-1 px-3 py-3 text-base border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-center font-semibold w-[70px] transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400"
                      />
                  
                      {/* Increment Minutes */}
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, minutes: Math.min(59, formData.minutes + 1)})}
                        className="w-11 h-11 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xl cursor-pointer flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-semibold"
                      >+</button>
                    </div>
                    <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors">mins</div>
                  </div>
                </div>
                {formData.hours === 0 && formData.minutes === 0 && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1 transition-colors">
                    Duration must be greater than 0
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
              Date <span className="text-red-500 dark:text-red-400 transition-colors">*</span>
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-cyan-400 focus:border-blue-500 dark:focus:border-cyan-400 text-base transition-colors font-medium"
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 transition-colors">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px]"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Entry'
              )}
            </button>
          </div>
        </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ManualEntryModal;
